import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { RfqProject, RfqSubmission, TimberRfqDrynessSpec, TimberSupplierStructureRef } from '../../types';
import { loadDealProposalStructures } from '../../utils/loadDealProposalStructures';
import {
  normalizeQuotedStructures,
  timberQuoteStructurePickerOptions,
} from '../../utils/timberQuoteStreamKey';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { updateLegacyQuoteSubmission } from '../../utils/legacyQuoteEdit';
import { parseQuoteTotalInput, submissionQuoteTotal } from '../../utils/rfqQuoteTotal';
import { parseLeadTimeWeeksRangeInput, submissionLeadTimeWeeksRange } from '../../utils/rfqLeadTimeWeeks';
import { subscribeToTimberItems, type TimberItem } from '../../utils/materialsDatabaseService';
import {
  allSelectableTimberSpecies,
  inferQuotedSpeciesFromFileName,
  rfqProjectTimberSpeciesDryness,
  submissionQuotedDryness,
  submissionQuotedSpecies,
} from '../../utils/timberRfqSuppliers';
import { DEFAULT_TIMBER_RFQ_DRYNESS_SPEC, normalizeTimberRfqDrynessSpec } from '../../utils/supplierTimberExport';
import { isSubmissionApprovedForRelease } from '../../utils/timberRfqPrePortalVersion';
import './LegacyQuoteEditModal.css';

export type LegacyQuoteEditModalProps = {
  open: boolean;
  onClose: () => void;
  project: RfqProject;
  submission: RfqSubmission | null;
  supplierOrgId: string;
  supplierName: string;
  packageId: string;
  releaseId: string;
  onSuccess?: () => void;
};

function dateInputValue(d?: Date): string {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function buildSpeciesExtras(sub: RfqSubmission): string[] {
  const extras: string[] = [];
  if (sub.quotedSpecies?.trim()) extras.push(sub.quotedSpecies.trim());
  const inferred = inferQuotedSpeciesFromFileName(sub.fileName ?? '', []);
  if (inferred) extras.push(inferred);
  return extras;
}

function resolveSubmissionSpecies(
  sub: RfqSubmission,
  timberItems: TimberItem[],
  draftSpecies = '',
): string {
  const extras = buildSpeciesExtras(sub);
  if (draftSpecies.trim()) extras.push(draftSpecies.trim());
  const catalog = allSelectableTimberSpecies(timberItems, extras);
  return (
    submissionQuotedSpecies(sub, catalog)
    ?? inferQuotedSpeciesFromFileName(sub.fileName ?? '', catalog)
    ?? ''
  );
}

const LegacyQuoteEditModal: React.FC<LegacyQuoteEditModalProps> = ({
  open,
  onClose,
  project,
  submission,
  supplierOrgId,
  supplierName,
  packageId,
  releaseId,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { updateRfqSubmission, updateRfqProject, rfqPackages, rfqSubmissions } = useCRM();

  const [timberItems, setTimberItems] = useState<TimberItem[]>([]);
  const submissionsByPackageId = useMemo(() => {
    const m = new Map<string, RfqSubmission[]>();
    for (const s of rfqSubmissions) {
      if (!s.rfqPackageId) continue;
      const list = m.get(s.rfqPackageId) ?? [];
      list.push(s);
      m.set(s.rfqPackageId, list);
    }
    return m;
  }, [rfqSubmissions]);

  const projectPackages = useMemo(
    () => rfqPackages.filter(p => p.rfqProjectId === project.id),
    [rfqPackages, project.id],
  );

  const pkgForEdit = useMemo(
    () => projectPackages.find(p => p.id === packageId),
    [projectPackages, packageId],
  );

  const [species, setSpecies] = useState('');
  const [dryness, setDryness] = useState<TimberRfqDrynessSpec>(DEFAULT_TIMBER_RFQ_DRYNESS_SPEC);
  const [quoteTotal, setQuoteTotal] = useState('');
  const [leadTimeWeeksMin, setLeadTimeWeeksMin] = useState('');
  const [leadTimeWeeksMax, setLeadTimeWeeksMax] = useState('');
  const [receivedDate, setReceivedDate] = useState('');
  const [markApproved, setMarkApproved] = useState(false);
  const [replaceFile, setReplaceFile] = useState<File | null>(null);
  const [selectedStructureIds, setSelectedStructureIds] = useState<string[]>([]);
  const [loadedProposalStructures, setLoadedProposalStructures] = useState<TimberSupplierStructureRef[]>(
    [],
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !project.dealId) {
      setLoadedProposalStructures([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const structures = await loadDealProposalStructures(project.dealId, { hubProjectId: project.timberProjectId });
      if (!cancelled) setLoadedProposalStructures(structures);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project.dealId]);

  const structureOptions = useMemo(
    () =>
      timberQuoteStructurePickerOptions(
        project,
        supplierOrgId,
        pkgForEdit,
        loadedProposalStructures,
      ),
    [project, supplierOrgId, pkgForEdit, loadedProposalStructures],
  );
  const [error, setError] = useState<string | null>(null);
  const lastInitializedSubmissionIdRef = useRef<string | null>(null);

  useEffect(() => subscribeToTimberItems(setTimberItems), []);

  const speciesOptions = useMemo(() => {
    if (!submission) return allSelectableTimberSpecies(timberItems, []);
    const extras = buildSpeciesExtras(submission);
    if (species.trim()) extras.push(species.trim());
    return allSelectableTimberSpecies(timberItems, extras);
  }, [timberItems, submission, species]);

  useEffect(() => {
    if (!open || !submission?.id) {
      if (!open) lastInitializedSubmissionIdRef.current = null;
      return;
    }

    if (lastInitializedSubmissionIdRef.current !== submission.id) {
      lastInitializedSubmissionIdRef.current = submission.id;
      setSpecies(resolveSubmissionSpecies(submission, timberItems));
      setDryness(
        submission.quotedDryness
          ? normalizeTimberRfqDrynessSpec(submission.quotedDryness)
          : (() => {
            const tagged = resolveSubmissionSpecies(submission, timberItems);
            return tagged
              ? rfqProjectTimberSpeciesDryness(project, supplierOrgId, tagged)
              : DEFAULT_TIMBER_RFQ_DRYNESS_SPEC;
          })(),
      );
      const total = submissionQuoteTotal(submission);
      setQuoteTotal(total != null ? String(total) : '');
      const leadRange = submissionLeadTimeWeeksRange(submission);
      setLeadTimeWeeksMin(leadRange?.min != null ? String(leadRange.min) : '');
      setLeadTimeWeeksMax(leadRange?.max != null ? String(leadRange.max) : '');
      setReceivedDate(dateInputValue(submission.createdAt));
      setMarkApproved(
        isSubmissionApprovedForRelease(
          project,
          projectPackages,
          submissionsByPackageId,
          releaseId,
          submission.id,
        ),
      );
      setReplaceFile(null);
      const quoted = normalizeQuotedStructures(submission.quotedStructures);
      if (quoted.length) {
        setSelectedStructureIds(quoted.map((s) => s.id));
      } else if (structureOptions.length === 1) {
        setSelectedStructureIds([structureOptions[0]!.id]);
      } else {
        setSelectedStructureIds([]);
      }
      setError(null);
      return;
    }

    setSpecies(prev => (prev.trim() ? prev : resolveSubmissionSpecies(submission, timberItems, prev)));
  }, [open, submission, project, supplierOrgId, timberItems, releaseId, projectPackages, submissionsByPackageId, structureOptions]);

  if (!open || !submission?.id) return null;

  const isLegacyFlow =
    submission.legacyImport === true || submission.staffManualUpload === true;

  const handleClose = () => {
    if (busy) return;
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!species.trim()) {
      setError('Select a species.');
      return;
    }
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      setError('Enter a valid total quote amount.');
      return;
    }
    const leadRange = parseLeadTimeWeeksRangeInput(leadTimeWeeksMin, leadTimeWeeksMax);
    if (!leadRange) {
      setError('Enter min and max lead time in whole weeks (min must be ≤ max).');
      return;
    }

    let quotedStructures: TimberSupplierStructureRef[] | undefined;
    if (structureOptions.length > 0) {
      if (structureOptions.length >= 2 && selectedStructureIds.length === 0) {
        setError('Select at least one structure this quote applies to.');
        return;
      }
      quotedStructures = selectedStructureIds.length
        ? normalizeQuotedStructures(
            structureOptions.filter((s) => selectedStructureIds.includes(s.id)),
          )
        : [];
    }

    setBusy(true);
    try {
      await updateLegacyQuoteSubmission(
        { updateRfqSubmission, updateRfqProject },
        project,
        supplierOrgId,
        submission,
        packageId,
        releaseId,
        {
          species: species.trim(),
          dryness,
          quoteTotal: total,
          leadTimeWeeksMin: leadRange.min,
          leadTimeWeeksMax: leadRange.max,
          receivedAt: receivedDate ? new Date(`${receivedDate}T12:00:00`) : undefined,
          replacePdfFile: replaceFile ?? undefined,
          markApproved,
          ...(quotedStructures !== undefined ? { quotedStructures } : {}),
        },
        user?.id
          ? {
              byUserId: user.id,
              byUserName: user.name || user.email || 'User',
              orgName: supplierName,
            }
          : undefined,
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lqi-modal-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="lqi-modal lqi-modal--timber"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lqe-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="lqi-modal-head">
          <div>
            <h2 id="lqe-modal-title">
              {isLegacyFlow ? 'Edit pre-portal quote' : 'Edit supplier quote input'}
            </h2>
            <p className="lqi-modal-sub">
              {isLegacyFlow
                ? 'Update species, dryness, amount, lead time range, or PDF for a quote imported before the supplier portal existed.'
                : 'Correct totals, lead time, species, or structure scope on this supplier quote. Updates timeline and forecast coverage; does not re-send the RFQ. Saves are internal-only and marked as PM adjusted input.'}
            </p>
          </div>
          <button type="button" className="lqi-modal-close" onClick={handleClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>

        <form className="lqi-modal-body" noValidate onSubmit={e => void handleSubmit(e)}>
          {structureOptions.length > 0 && (
            <fieldset className="lqi-field" disabled={busy} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
              <legend style={{ fontSize: 12, fontWeight: 600 }}>Structures this quote applies to</legend>
              {structureOptions.map((s) => (
                <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={selectedStructureIds.includes(s.id)}
                    onChange={() =>
                      setSelectedStructureIds((prev) =>
                        prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id],
                      )
                    }
                  />
                  <span>{s.name || s.id}</span>
                </label>
              ))}
            </fieldset>
          )}
          <label className="lqi-field">
            <span>Supplier</span>
            <input type="text" value={supplierName} disabled readOnly aria-readonly />
          </label>

          <div className="lqi-species-quotes">
            <div className="lqi-species-quotes-head">
              <span className="lqi-species-quotes-title">Quote details</span>
            </div>

            <div className="lqi-species-quote-line">
              <label className="lqi-field lqi-field--inline">
                <span>Species</span>
                <select
                  value={species}
                  disabled={busy}
                  onChange={e => {
                    const next = e.target.value;
                    setSpecies(next);
                    if (next && !submission.quotedDryness) {
                      setDryness(rfqProjectTimberSpeciesDryness(project, supplierOrgId, next));
                    }
                  }}
                >
                  <option value="">Select species…</option>
                  {speciesOptions.map(sp => (
                    <option key={sp} value={sp}>
                      {sp}
                    </option>
                  ))}
                </select>
              </label>

              <label className="lqi-field lqi-field--inline">
                <span>Dryness</span>
                <select
                  value={dryness}
                  disabled={busy}
                  onChange={e => setDryness(e.target.value as TimberRfqDrynessSpec)}
                >
                  <option value="green">Green</option>
                  <option value="kiln_dried">Kiln-Dried</option>
                </select>
              </label>

              <label className="lqi-field lqi-field--inline">
                <span>Total ($)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={quoteTotal}
                  disabled={busy}
                  required
                  placeholder="e.g. 85000"
                  onChange={e => setQuoteTotal(e.target.value)}
                />
              </label>

              <label className="lqi-field lqi-field--inline">
                <span>Lead min (wk)</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={leadTimeWeeksMin}
                  disabled={busy}
                  required
                  placeholder="4"
                  onChange={e => setLeadTimeWeeksMin(e.target.value)}
                />
              </label>

              <label className="lqi-field lqi-field--inline">
                <span>Lead max (wk)</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={leadTimeWeeksMax}
                  disabled={busy}
                  required
                  placeholder="5"
                  onChange={e => setLeadTimeWeeksMax(e.target.value)}
                />
              </label>

              <label className="lqi-field lqi-field--inline">
                <span>Quote PDF</span>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  disabled={busy}
                  onChange={e => setReplaceFile(e.target.files?.[0] ?? null)}
                />
                {replaceFile ? (
                  <span className="lqi-file-name">{replaceFile.name}</span>
                ) : submission.fileName ? (
                  <span className="lqi-file-name">Current: {submission.fileName}</span>
                ) : null}
              </label>
            </div>
          </div>

          {isLegacyFlow && (
            <label className="lqi-field">
              <span>Quote received (optional)</span>
              <input
                type="date"
                value={receivedDate}
                disabled={busy}
                onChange={e => setReceivedDate(e.target.value)}
              />
            </label>
          )}

          {isLegacyFlow && user?.role !== 'staff' && (
            <label className="lqi-check">
              <input
                type="checkbox"
                checked={markApproved}
                disabled={busy}
                onChange={e => setMarkApproved(e.target.checked)}
              />
              <span>Mark quote as approved</span>
            </label>
          )}

          {error && <p className="lqi-error">{error}</p>}

          <div className="lqi-modal-actions">
            <button type="button" className="ops-hide-completed-btn" disabled={busy} onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="ops-view-btn active" disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LegacyQuoteEditModal;
